import { useState, useEffect } from 'react';
import axios from '@common/axios';

const useUserAnnotations = () => {
    const [favorites, setFavorites] = useState([]);
    const [favoritesLoading, setFavoritesLoading] = useState(true);

    const [notes, setNotes] = useState(() => {
        try {
            const saved = localStorage.getItem('hotmaths_notes');
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    });

    const [showNotesModal, setShowNotesModal] = useState(false);
    const [currentNoteText, setCurrentNoteText] = useState('');
    const [notesQuestionId, setNotesQuestionId] = useState(null);

    // Load favorites from backend on mount
    useEffect(() => {
        const loadFavorites = async () => {
            try {
                const response = await axios.get('/web/ui/question/favorites');
                setFavorites(response.data || []);
            } catch (err) {
                console.error('Failed to load favorites:', err);
                setFavorites([]);
            } finally {
                setFavoritesLoading(false);
            }
        };
        loadFavorites();
    }, []);

    useEffect(() => {
        try { localStorage.setItem('hotmaths_notes', JSON.stringify(notes)); } catch {}
    }, [notes]);

    const handleToggleFavorite = async (questionId) => {
        // Optimistic update
        const wasFavorite = favorites.includes(questionId);
        setFavorites(prev => wasFavorite ? prev.filter(id => id !== questionId) : [...prev, questionId]);

        try {
            await axios.post(`/web/ui/question/favorites/${questionId}`);
        } catch (err) {
            console.error('Failed to toggle favorite:', err);
            // Rollback on error
            setFavorites(prev => wasFavorite ? [...prev, questionId] : prev.filter(id => id !== questionId));
        }
    };

    const handleOpenNotes = (questionId) => {
        setNotesQuestionId(questionId);
        setCurrentNoteText(notes[questionId] || '');
        setShowNotesModal(true);
    };

    const handleSaveNote = () => {
        if (!notesQuestionId) return;
        setNotes(prev => ({ ...prev, [notesQuestionId]: currentNoteText }));
        setShowNotesModal(false);
        setNotesQuestionId(null);
    };

    const handleDeleteNote = () => {
        if (!notesQuestionId) return;
        setNotes(prev => { const s = { ...prev }; delete s[notesQuestionId]; return s; });
        setCurrentNoteText('');
        setShowNotesModal(false);
        setNotesQuestionId(null);
    };

    return {
        favorites,
        favoritesLoading,
        handleToggleFavorite,
        notes,
        showNotesModal,
        setShowNotesModal,
        currentNoteText,
        setCurrentNoteText,
        notesQuestionId,
        setNotesQuestionId,
        handleOpenNotes,
        handleSaveNote,
        handleDeleteNote
    };
};

export default useUserAnnotations;
